'use client';

import { useMemo, useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import StyledQRCode from '@/components/StyledQRCode';
import { getNetworkNameById } from '@/utils/networkNames';
import type { VerificationTemplateProps } from './types';

function resolveLogoSrc(raw: string | null | undefined) {
    const value = (raw ?? '').trim();
    if (!value) return null;
    if (/^(https?:\/\/|data:)/.test(value)) return value;
    if (value.startsWith('/')) {
        const apiBase = (process.env.NEXT_PUBLIC_API_BASE || '').replace(/\/+$/, '');
        return apiBase ? `${apiBase}${value}` : value;
    }
    return value;
}

function ipfsToHttp(uri: string) {
    return uri.replace('ipfs://', 'https://ipfs.io/ipfs/');
}

export default function TemplateCardQr({
    certificate,
    verificationUrl,
    tenant,
    qrUrl,
}: VerificationTemplateProps) {
    const issuerDisplayName = tenant?.issuerName || tenant?.name || 'Sigalmark';

    const [issuedDisplay, setIssuedDisplay] = useState('');
    const [expiryDisplay, setExpiryDisplay] = useState('');

    useEffect(() => {
        setIssuedDisplay(
            new Date(certificate.issued).toLocaleString(undefined, { timeZone: 'UTC' }),
        );
        setExpiryDisplay(
            new Date(certificate.expiry).toLocaleString(undefined, { timeZone: 'UTC' }),
        );
    }, [certificate.issued, certificate.expiry]);

    const statusIcon = useMemo(() => {
        switch (certificate.status) {
            case 'valid':
                return '✅';
            case 'revoked':
                return '🚫';
            case 'expired':
                return '⏰';
            default:
                return '❓';
        }
    }, [certificate.status]);

    const qrLogo = resolveLogoSrc(tenant?.logoUrl) || '/sigalmark_logo.png';

    return (
        <div className="p-6 space-y-8 max-w-2xl mx-auto">
            <h1 className="text-2xl font-bold">Certificate verification</h1>
            <p className="text-sm text-muted-foreground">
                Issuer: <span className="font-medium text-foreground">{issuerDisplayName}</span>
            </p>

            <Card className="mt-6">
                <CardContent className="p-4 space-y-2">
                    <p>
                        <strong>Status:</strong> {statusIcon} {certificate.status.toUpperCase()}
                    </p>
                    <p>
                        <strong>Name:</strong> {certificate.name}
                    </p>
                    <p>
                        <strong>Competency:</strong> {certificate.competency}
                    </p>
                    <p>
                        <strong>Issued:</strong> {issuedDisplay}
                    </p>
                    <p>
                        <strong>Expiry:</strong> {expiryDisplay}
                    </p>
                    <hr className="my-2" />
                    <p>
                        <strong>Contract Address:</strong>{' '}
                        <span className="break-all text-muted-foreground">{certificate.contract}</span>
                    </p>
                    <p>
                        <strong>Token ID:</strong> {certificate.tokenId}
                    </p>
                    <p>
                        <strong>Network:</strong> {getNetworkNameById(certificate.networkId)}
                    </p>

                    {certificate.imageURI && (
                        <div className="w-full max-w-xs rounded-xl overflow-hidden border shadow">
                            <img
                                src={ipfsToHttp(certificate.imageURI)}
                                alt="Certificate NFT"
                                className="w-full h-auto transform hover:scale-110 transition-transform duration-300"
                            />
                        </div>
                    )}

                    {certificate.explorerUrl && (
                        <p className="text-sm text-muted-foreground break-all">
                            <strong>Explorer:</strong>{' '}
                            <a
                                href={certificate.explorerUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="underline text-primary hover:text-primary/80"
                            >
                                View on Explorer
                            </a>
                        </p>
                    )}

                    <StyledQRCode
                        url={qrUrl}
                        styleKey="greenDots"
                        image={qrLogo}
                        tintElements={tenant?.tintElements ?? undefined}
                        tintText={tenant?.tintText ?? undefined}
                        tintBackground={tenant?.tintBackground ?? undefined}
                    />
                    <p className="text-xs break-all text-muted-foreground">{verificationUrl}</p>
                </CardContent>
            </Card>
        </div>
    );
}
